import React, { useMemo, useState } from 'react';

import Icon from './Icon';

import type { Event as BsmEvent } from '../../interfaces/event/event.interface';
import type { Employee } from '../../interfaces/employee/employee.interface';
import type { LeaveRequest } from '../../interfaces/employee/leave-request.interface';

// =============================================================================
//  MonthCalendar — v3
//
//  Port of the legacy dashboard's sidebar calendar, restyled for v3.  Marks
//  each day that carries any of:
//    · an event            (event_store)
//    · an employee birthday (month/day match, year-agnostic)
//    · an approved leave day (weekdays between start_date and end_date)
//
//  Legend dots use the same three marker classes so a day can carry more than
//  one kind of marker at once.  Selecting a day raises it to the parent so the
//  agenda panel can show that day's detail.
// =============================================================================

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

// Monday-first, matching the legacy calendar.
const WEEKDAY_HEADS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export interface DayMarkers {
  event: boolean;
  birthday: boolean;
  leave: boolean;
}

/** Local YYYY-MM-DD key.  Avoids toISOString(), which shifts across the UTC
 *  boundary and can land a marker on the wrong day in +02:00. */
const dayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
    2,
    '0'
  )}`;

const isSameDay = (a: Date, b: Date): boolean => dayKey(a) === dayKey(b);

/** Monday-indexed offset (Mon = 0 … Sun = 6) of the 1st of the month. */
const firstWeekdayOffset = (year: number, month: number): number => {
  const jsDay = new Date(year, month, 1).getDay(); // Sun = 0
  return jsDay === 0 ? 6 : jsDay - 1;
};

interface Props {
  events: BsmEvent[];
  employees: Employee[];
  leaveRequests: LeaveRequest[];
  selected?: Date;
  onSelectDay?: (date: Date | undefined) => void;
}

const MonthCalendar = ({ events, employees, leaveRequests, selected, onSelectDay }: Props) => {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState<Date>(() => new Date(today.getFullYear(), today.getMonth()));

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  // Build a day-key → markers map once per data/month change.
  const markers = useMemo(() => {
    const map = new Map<string, DayMarkers>();
    const mark = (d: Date, kind: keyof DayMarkers) => {
      if (Number.isNaN(d.getTime())) return;
      const key = dayKey(d);
      const cur = map.get(key) ?? { event: false, birthday: false, leave: false };
      cur[kind] = true;
      map.set(key, cur);
    };

    events.forEach((e) => e.date && mark(new Date(e.date), 'event'));

    // Birthdays recur — project onto the year currently in view.
    employees.forEach((emp) => {
      if (!emp.birthday) return;
      const b = new Date(emp.birthday);
      if (Number.isNaN(b.getTime())) return;
      mark(new Date(year, b.getMonth(), b.getDate()), 'birthday');
    });

    // Approved leave only, weekdays only (mirrors the legacy behaviour).
    leaveRequests.forEach((leave) => {
      if (leave.status !== 'approved') return;
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
      const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      // Guard against inverted/absurd ranges walking forever.
      let guard = 0;
      while (cur <= end && guard < 400) {
        const dow = cur.getDay();
        if (dow !== 0 && dow !== 6) mark(new Date(cur), 'leave');
        cur.setDate(cur.getDate() + 1);
        guard += 1;
      }
    });

    return map;
  }, [events, employees, leaveRequests, year]);

  const weeks = useMemo(() => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const offset = firstWeekdayOffset(year, month);
    const cells: Array<Date | null> = [
      ...Array.from({ length: offset }, () => null),
      ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1))
    ];
    while (cells.length % 7 !== 0) cells.push(null);
    const rows: Array<Array<Date | null>> = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [year, month]);

  const step = (delta: number) => {
    setCursor(new Date(year, month + delta));
    onSelectDay?.(undefined);
  };

  const handlePick = (d: Date) => {
    if (selected && isSameDay(selected, d)) {
      onSelectDay?.(undefined); // toggle off
    } else {
      onSelectDay?.(d);
    }
  };

  return (
    <div className="v3-cal">
      <div className="v3-cal-head">
        <span className="v3-cal-title">
          {MONTHS[month]} {year}
        </span>
        <div className="v3-cal-nav">
          <button type="button" aria-label="Previous month" onClick={() => step(-1)}>
            <Icon name="back" size={13} />
          </button>
          <button
            type="button"
            className="v3-cal-today"
            onClick={() => {
              setCursor(new Date(today.getFullYear(), today.getMonth()));
              onSelectDay?.(undefined);
            }}
          >
            Today
          </button>
          <button type="button" aria-label="Next month" onClick={() => step(1)}>
            <Icon name="arrow" size={13} />
          </button>
        </div>
      </div>

      <table className="v3-cal-grid">
        <thead>
          <tr>
            {WEEKDAY_HEADS.map((w) => (
              <th key={w} scope="col">
                {w}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((row, ri) => (
            <tr key={`w-${ri}`}>
              {row.map((d, di) => {
                if (!d) return <td key={`e-${ri}-${di}`} className="empty" />;
                const m = markers.get(dayKey(d));
                const classes = [
                  isSameDay(d, today) ? 'today' : '',
                  selected && isSameDay(d, selected) ? 'selected' : '',
                  m ? 'has-marker' : ''
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <td key={dayKey(d)} className={classes}>
                    <button type="button" onClick={() => handlePick(d)}>
                      <span className="d">{d.getDate()}</span>
                      <span className="dots">
                        {m?.event && <i className="dot event" />}
                        {m?.birthday && <i className="dot birthday" />}
                        {m?.leave && <i className="dot leave" />}
                      </span>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="v3-cal-legend">
        <span>
          <i className="dot event" /> Event
        </span>
        <span>
          <i className="dot birthday" /> Birthday
        </span>
        <span>
          <i className="dot leave" /> Leave
        </span>
      </div>
    </div>
  );
};

export default MonthCalendar;
